import {
    NinchatProperties,
    NinchatState,
} from 'api/types'
import {
    listActiveChannels,
    listActiveDialogues,
    listActiveQueues,
} from 'selectors/ninchat'
import {
    useAppDispatch,
    useAppSelector,
} from 'hooks'

import {
    useChannelDetails,
    useRealms,
    useUserDetails,
} from 'hooks/ninchat'
import Button from 'components/Button'
import {JitsiState} from 'reducers/jitsi'
import LetterAvatar from 'components/Avatar/LetterAvatar'
import React from 'react'
import UserAvatar from 'components/Avatar/UserAvatar'
import {jitsiToggleChat} from 'actions/jitsi'
import {navigation} from 'navigation'
import {styled} from 'stitches/config'
import {styledItem} from 'components/Dropdown/styles'
import {styledName} from 'components/Sidebar/styles'
import {updateRealm} from 'state/ui'
import {useTranslation} from 'react-i18next'

const StyledContainer = styled('div', {
    display: 'flex',
    flexDirection: 'column',
})
const StyledName = styled('div', styledName, {
    display: 'flex',
    flexDirection: 'column',
})
const StyledItem = styled('div', styledItem)
const StyledSmall = styled('small', {
    color: '$buttonText',
    fontWeight: 'normal',
})
const StyledSmallOrganizationName = styled('small', {
    color: '$organizationNameLightText',
    fontWeight: 'normal',
})

const ActivityChannelView = ({channelId}:{
    channelId: string
}): React.ReactElement => {
    const channelDetails = useChannelDetails({channelId})
    return (
        <>
            <LetterAvatar
                highlight={channelDetails.channelStatus === 'highlight'}
                name={channelDetails.name}
                size='small'
                unread={channelDetails.channelStatus === 'unread'}
            />
            <StyledName status={channelDetails.channelStatus}>
                <div title={channelDetails.name}>{channelDetails.name}</div>
            </StyledName>
        </>
    )
}

const ActivityDialogueView = ({
    dialogueStatus,
    userId,
}:{
    userId: string, dialogueStatus?: NinchatProperties.DialogueStatus
}): React.ReactElement => {
    const userDetails = useUserDetails({
        dialogueStatus,
        userId,
    })
    return (
        <>
            <UserAvatar
                connected={userDetails.connected}
                highlight={userDetails.dialogueStatus === 'highlight'}
                idle={Boolean(userDetails.idle)}
                name={userDetails.name}
                size='small'
                unread={userDetails.dialogueStatus === 'unread'}
            />
            <StyledName status={userDetails.dialogueStatus}>
                <div title={userDetails.name}>{userDetails.name}</div>
            </StyledName>
        </>
    )
}
ActivityDialogueView.defaultProps = {dialogueStatus: undefined}

const ActivityView: React.FunctionComponent = () => {
    const activeChannels = useAppSelector(({ninchat}: {ninchat: NinchatState}) => listActiveChannels(ninchat))
    const activeDialogues = useAppSelector(({ninchat}: {ninchat: NinchatState}) => listActiveDialogues(ninchat))
    const activeQueues = useAppSelector(({
        ninchat, ui,
    }) => listActiveQueues(ninchat, ui.queue_pickup_delays))
    const isChatOpen = useAppSelector(({jitsi}: {jitsi: JitsiState}) => jitsi.isChatOpen)

    const realms = useRealms()
    const hasMultipleRealms = Object.keys(realms).length > 1
    const {t} = useTranslation()

    const dispatch = useAppDispatch()

    const handleChannel = (channel: typeof activeChannels[number]) => {
        // if chat is not open, open it
        if (!isChatOpen) {
            dispatch(jitsiToggleChat())
        }
        navigation.push('Channel', {channel_id: channel.channel_id})
        if (channel.realm_id) {
            dispatch(updateRealm(channel.realm_id))
        }
    }

    const handleDialogue = (dialogue: typeof activeDialogues[number]) => navigation.push('Dialogue', {dialogue_id: dialogue.dialogue_id})

    const handleQueue = (queue: typeof activeQueues[number]) => {
        navigation.push('Queue', {queue_id: queue.queue_id})
        if (queue.realm_id) {
            dispatch(updateRealm(queue.realm_id))
        }
    }

    const realmName = (realmId: string | undefined) => realms[realmId ?? '']?.realm_attrs.name ?? ''

    return (
        <StyledContainer>
            {activeQueues
                .map((queue) => (
                    <StyledItem key={queue.queue_id}>
                        <Button color='text' onPress={() => handleQueue(queue)} type='button'>
                            <LetterAvatar
                                highlight={true}
                                name={queue.queue_attrs.name}
                                size='small'
                            />
                            <StyledName status='highlight'>
                                <div title={queue.queue_attrs.name}>{queue.queue_attrs.name}</div>
                                {hasMultipleRealms ?
                                    <StyledSmallOrganizationName> {realmName(queue.realm_id)} </StyledSmallOrganizationName>
                                    : null}
                                <StyledSmall>{t('{{count}} visitors in queue', {count: queue.queue_attrs.length})}</StyledSmall>
                                {(queue.queue_attrs.unassigned_registered ?? 0) > 0 &&
                                <StyledSmall>{t('{{count}} registered contacts', {count: queue.queue_attrs.unassigned_registered})}</StyledSmall>}
                            </StyledName>
                        </Button>
                    </StyledItem>
                ))}
            {activeDialogues.map((dialogue) => (
                <StyledItem key={dialogue.dialogue_id}>
                    <Button color='text' onPress={() => handleDialogue(dialogue)} type='button'>
                        <ActivityDialogueView dialogueStatus={dialogue.dialogue_status} userId={dialogue.dialogue_id} />
                    </Button>
                </StyledItem>
            ))}
            {activeChannels.map((channel) => (
                <StyledItem key={channel.channel_id}>
                    <Button color='text' onPress={() => handleChannel(channel)} type='button'>
                        <ActivityChannelView channelId={channel.channel_id} />
                    </Button>
                </StyledItem>
            ))}
        </StyledContainer>
    )
}

export default ActivityView
